// src/pages/Developer.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet';
import { 
  getAuth, 
  signInWithPopup, 
  GoogleAuthProvider, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import DefaultAvatarIcon from '../assets/avataricon.jpg';

// Square Payment Links
const SQUARE_LINKS = {
  starter: "https://square.link/u/HMxTh8Vw",
  creatorMonthly: "https://square.link/u/fa1iCLWL",
  creatorAnnual: "https://square.link/u/m46OyUxC",
  proMonthly: "https://square.link/u/o25DM4sO",
  proAnnual: "https://square.link/u/JcndaVXT"
};

// Plan definitions
const PLANS = {
  monthly: [
    {
      name: "Starter", desc: "3-day free trial. Full access to our AI interior design editor.",
      price: "$0",
      features: ["3-day free trial", "Generate up to 100 images OR 2-3 videos (7-15s each)", "HD resolution exports (1080p)", "Basic AI style suggestions", "No watermark on exports", "Save up to 5 design projects", "Video export (H.264, .MOV)", "Email support within 48 hours"],
      cta: "Start Free Trial", squareLink: SQUARE_LINKS.starter, planType: "starter", billingCycle: "trial"
    },
    {
      name: "Creator", desc: "For interior designers building their portfolio and client work.",
      price: "$59",
      features: ["Generate up to 1,300 images OR 25-30 videos (7-15s each) per month", "4K resolution exports", "Advanced AI style suggestions", "Precision perspective matching", "Commercial usage rights", "3D model import capabilities", "Unlimited design projects", "Video export (H.264, .MOV, ProRes)", "Priority email support within 24 hours", "Usage limits reset each billing cycle"],
      cta: "Choose Creator", squareLink: SQUARE_LINKS.creatorMonthly, planType: "creator", billingCycle: "monthly"
    },
    {
      name: "Pro", desc: "For established designers and studios serving multiple clients.",
      price: "$199",
      features: ["Generate up to 5,500 images OR 120-135 videos (7-15s each) per month", "8K resolution exports", "Advanced 3D model manipulation", "Batch export processing", "Client presentation tools", "Commercial usage rights", "Dedicated priority support", "Early access to new features", "Personalized AI recommendations", "Unlimited design projects", "Video export (H.264, .MOV, ProRes, RAW)", "Usage limits reset each billing cycle"],
      cta: "Choose Pro", squareLink: SQUARE_LINKS.proMonthly, planType: "pro", billingCycle: "monthly"
    }
  ],
  annual: [
    {
      name: "Starter", desc: "3-day free trial. Full access to our AI interior design editor.",
      price: "$0",
      features: ["3-day free trial", "Generate up to 100 images OR 2-3 videos (7-15s each)", "HD resolution exports (1080p)", "Basic AI style suggestions", "No watermark on exports", "Save up to 5 design projects", "Video export (H.264, .MOV)", "Email support within 48 hours"],
      cta: "Start Free Trial", squareLink: SQUARE_LINKS.starter, planType: "starter", billingCycle: "trial"
    },
    {
      name: "Creator", desc: "For interior designers building their portfolio and client work.",
      price: "$588", saving: "$120",
      features: ["Generate up to 1,300 images OR 25-30 videos (7-15s each) per month", "4K resolution exports", "Advanced AI style suggestions", "Precision perspective matching", "Commercial usage rights", "3D model import capabilities", "Unlimited design projects", "Video export (H.264, .MOV, ProRes)", "Priority email support within 24 hours", "Usage limits reset each billing cycle"],
      cta: "Choose Creator", squareLink: SQUARE_LINKS.creatorAnnual, planType: "creator", billingCycle: "annual"
    },
    {
      name: "Pro", desc: "For established designers and studios serving multiple clients.",
      price: "$1,988", saving: "$400",
      features: ["Generate up to 5,500 images OR 120-135 videos (7-15s each) per month", "8K resolution exports", "Advanced 3D model manipulation", "Batch export processing", "Client presentation tools", "Commercial usage rights", "Dedicated priority support", "Early access to new features", "Personalized AI recommendations", "Unlimited design projects", "Video export (H.264, .MOV, ProRes, RAW)", "Usage limits reset each billing cycle"],
      cta: "Choose Pro", squareLink: SQUARE_LINKS.proAnnual, planType: "pro", billingCycle: "annual"
    }
  ]
};

const Developer = () => {
  // Dev login states
  const [devEmail, setDevEmail] = useState('');
  const [devPassword, setDevPassword] = useState('');
  const [devError, setDevError] = useState('');
  const [isDevAuthenticated, setIsDevAuthenticated] = useState(false); // Track if dev is logged in
  const [showFullAuth, setShowFullAuth] = useState(false);
  
  // Auth form states
  const [authMode, setAuthMode] = useState('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resetPasswordMode, setResetPasswordMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  // Profile completion states
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [profileStep, setProfileStep] = useState(1);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Plan selection states
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(1);
  const [hoveredPlanIndex, setHoveredPlanIndex] = useState(null);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const auth = getAuth();

  // Theme setup
  useEffect(() => {
    let metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.name = 'theme-color';
      document.head.appendChild(metaThemeColor);
    }
    metaThemeColor.content = '#FAFAFA';
    document.documentElement.style.backgroundColor = '#FAFAFA';
    document.body.style.backgroundColor = '#FAFAFA';
    return () => {
      document.documentElement.style.backgroundColor = '';
      document.body.style.backgroundColor = '';
    };
  }, []);

  // Load selected plan from session storage
  useEffect(() => {
    const planData = sessionStorage.getItem('selectedPlan');
    if (planData) {
      try {
        const parsedPlan = JSON.parse(planData);
        setSelectedPlan(parsedPlan);
        if (parsedPlan.billingCycle === 'annual') setBillingCycle('annual');
        else if (parsedPlan.billingCycle === 'monthly') setBillingCycle('monthly');
        const plansList = parsedPlan.billingCycle === 'annual' ? PLANS.annual : PLANS.monthly;
        const index = plansList.findIndex(p => p.planType === parsedPlan.planType);
        if (index !== -1) setSelectedPlanIndex(index);
      } catch (e) { console.error('Error parsing plan data:', e); }
    }
  }, []);

  const getSquareLink = (plan) => {
    if (!plan) return null;
    if (plan.planType === 'starter') return SQUARE_LINKS.starter;
    if (plan.planType === 'creator' && plan.billingCycle === 'monthly') return SQUARE_LINKS.creatorMonthly;
    if (plan.planType === 'creator' && plan.billingCycle === 'annual') return SQUARE_LINKS.creatorAnnual;
    if (plan.planType === 'pro' && plan.billingCycle === 'monthly') return SQUARE_LINKS.proMonthly;
    if (plan.planType === 'pro' && plan.billingCycle === 'annual') return SQUARE_LINKS.proAnnual;
    return null;
  };

  // ============================================
  // DEV LOGIN HANDLER
  // ============================================
  const handleDevSubmit = (e) => {
    e.preventDefault();
    const correctEmail = 'developer@outlne.com';
    const correctPassword = 'odev26'; // LEAVE BLANK FOR NOW

    if (devEmail === correctEmail && devPassword === correctPassword) {
      login();
      setDevError('');
      setIsDevAuthenticated(true); // Unlock the dashboard + test auth buttons
    } else {
      setDevError('Invalid email or password');
      setDevPassword('');
    }
  };

  // ============================================
  // GO TO DASHBOARD (after dev login)
  // ============================================
  const handleGoToDashboard = () => {
    sessionStorage.setItem('needsThemeReload', 'true');
    
    let metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.name = 'theme-color';
      document.head.appendChild(metaThemeColor);
    }
    
    const parent = metaThemeColor.parentNode;
    const clone = metaThemeColor.cloneNode(true);
    clone.content = '#2D2D2D';
    parent.removeChild(metaThemeColor);
    document.head.appendChild(clone);
    
    document.documentElement.style.backgroundColor = '#2D2D2D';
    document.body.style.backgroundColor = '#2D2D2D';
    
    setTimeout(() => {
      navigate('/dashboard');
    }, 50);
  };

  // ============================================
  // FULL AUTH FLOW FUNCTIONS (same as Auth New)
  // ============================================
  const checkUserProfileStatus = async (user) => {
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        await setDoc(userRef, { lastLogin: serverTimestamp(), lastActive: serverTimestamp() }, { merge: true }).catch(err => console.warn("Could not update lastLogin:", err));
        
        if (userData.profileComplete) {
          if (userData.subscriptionStatus === 'active' || userData.subscriptionStatus === 'trialing') {
            if (window.opener) { window.opener.postMessage('authSuccess', window.location.origin); window.close(); }
            else { navigate('/dashboard'); }
          } else if (userData.subscriptionStatus === 'pending_payment') {
            const plan = userData.pendingPlan || selectedPlan;
            const squareLink = getSquareLink(plan);
            if (squareLink) { sessionStorage.setItem('pendingUserId', user.uid); window.location.href = squareLink; }
            else { navigate('/pricing'); }
          } else {
            setCurrentUser(user);
            if (user.displayName) { const nameParts = user.displayName.split(' '); setFirstName(nameParts[0] || ''); setLastName(nameParts.slice(1).join(' ') || ''); }
            if (user.email) setEmail(user.email);
            setProfileStep(1); setShowProfileCompletion(true); setIsLoading(false);
          }
        } else {
          setCurrentUser(user);
          if (user.displayName) { const nameParts = user.displayName.split(' '); setFirstName(nameParts[0] || ''); setLastName(nameParts.slice(1).join(' ') || ''); }
          if (user.email) setEmail(user.email);
          setShowProfileCompletion(true); setIsLoading(false);
        }
      } else {
        setCurrentUser(user);
        if (user.displayName) { const nameParts = user.displayName.split(' '); setFirstName(nameParts[0] || ''); setLastName(nameParts.slice(1).join(' ') || ''); }
        if (user.email) setEmail(user.email);
        setShowProfileCompletion(true); setIsLoading(false);
      }
    } catch (error) {
      console.error('Error checking user profile:', error);
      setCurrentUser(user); setShowProfileCompletion(true); setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(''); setIsLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const creationTime = new Date(user.metadata.creationTime).getTime();
      const lastSignInTime = new Date(user.metadata.lastSignInTime).getTime();
      const timeDiff = Math.abs(lastSignInTime - creationTime);
      const isNewUser = timeDiff < 5000;
      
      if (isNewUser) {
        setCurrentUser(user);
        if (user.displayName) { const nameParts = user.displayName.split(' '); setFirstName(nameParts[0] || ''); setLastName(nameParts.slice(1).join(' ') || ''); }
        const userRef = doc(db, 'users', user.uid);
        await setDoc(userRef, { uid: user.uid, email: user.email, emailVerified: user.emailVerified, photoURL: user.photoURL || null, createdAt: serverTimestamp(), lastLogin: serverTimestamp(), provider: 'google.com', profileComplete: false, subscriptionStatus: 'none', accountStatus: 'trial_eligible' });
        if (selectedPlan) { await setDoc(userRef, { pendingPlan: { planType: selectedPlan.planType, billingCycle: selectedPlan.billingCycle, price: selectedPlan.price, name: selectedPlan.name, selectedAt: serverTimestamp() } }, { merge: true }); }
        setShowProfileCompletion(true); setIsLoading(false);
      } else {
        await checkUserProfileStatus(user); setIsLoading(false);
      }
    } catch (error) {
      console.error('Google Sign-In Error:', error); setIsLoading(false);
      if (error.code === 'auth/popup-closed-by-user') setError('Sign-in popup was closed. Please try again.');
      else setError(error.message);
    }
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault(); setError(''); setIsLoading(true);
    if (password.length < 6) { setError('Password must be at least 6 characters'); setIsLoading(false); return; }
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const user = result.user;
      setCurrentUser(user);
      const userRef = doc(db, 'users', user.uid);
      try {
        await setDoc(userRef, { uid: user.uid, email: user.email, emailVerified: user.emailVerified, photoURL: null, createdAt: serverTimestamp(), lastLogin: serverTimestamp(), provider: 'email', profileComplete: false, subscriptionStatus: 'none', accountStatus: 'trial_eligible' });
      } catch (firestoreError) { console.error('Firestore write failed:', firestoreError); setError('Failed to create user profile. Please try again.'); setIsLoading(false); return; }
      if (selectedPlan) { await setDoc(userRef, { pendingPlan: { planType: selectedPlan.planType, billingCycle: selectedPlan.billingCycle, price: selectedPlan.price, name: selectedPlan.name, selectedAt: serverTimestamp() } }, { merge: true }); }
      setShowProfileCompletion(true); setIsLoading(false);
    } catch (error) {
      console.error('Email Sign-Up Error:', error); setIsLoading(false);
      if (error.code === 'auth/email-already-in-use') setError('This email is already registered. Try logging in instead.');
      else if (error.code === 'auth/invalid-email') setError('Please enter a valid email address.');
      else setError(error.message);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault(); setError(''); setIsLoading(true);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const user = result.user;
      if (window.opener) { window.opener.postMessage('authSuccess', window.location.origin); }
      await checkUserProfileStatus(user); setIsLoading(false);
    } catch (error) {
      console.error('Email Login Error:', error); setIsLoading(false);
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') setError('Invalid email or password.');
      else if (error.code === 'auth/invalid-email') setError('Please enter a valid email address.');
      else setError(error.message);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault(); setError(''); setSuccessMessage(''); setIsLoading(true);
    if (!email) { setError('Please enter your email address'); setIsLoading(false); return; }
    try { await sendPasswordResetEmail(auth, email); setSuccessMessage('Password reset email sent! Check your inbox.'); setIsLoading(false); }
    catch (error) { console.error('Password Reset Error:', error); setIsLoading(false); if (error.code === 'auth/user-not-found') setError('No account found with this email address.'); else setError(error.message); }
  };

  const handleProfileStep1 = (e) => { e.preventDefault(); setError(''); if (!firstName.trim() || !lastName.trim()) { setError('First and last name are required'); return; } setProfileStep(2); };

  const handleProfileStep2 = (e) => {
    e.preventDefault(); setError('');
    if (!birthMonth || !birthDay || !birthYear) { setError('Please enter your full birth date'); return; }
    const month = parseInt(birthMonth); const day = parseInt(birthDay); const year = parseInt(birthYear);
    if (month < 1 || month > 12) { setError('Please enter a valid month (1-12)'); return; }
    if (day < 1 || day > 31) { setError('Please enter a valid day (1-31)'); return; }
    const currentYear = new Date().getFullYear();
    if (year < 1900 || year > currentYear) { setError(`Please enter a valid year (1900-${currentYear})`); return; }
    const birthDate = new Date(year, month - 1, day); const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
    if (age < 13) { setError('You must be at least 13 years old to use Outlne'); return; }
    setProfileStep(3);
  };

  const handlePlanSelection = async (e) => {
    e.preventDefault(); setError(''); setIsSubmitting(true);
    try {
      const selectedPlanData = PLANS[billingCycle][selectedPlanIndex];
      const userRef = doc(db, 'users', currentUser.uid);
      const displayNameValue = displayName.trim() || `${firstName} ${lastName}`;
      const month = parseInt(birthMonth); const day = parseInt(birthDay); const year = parseInt(birthYear);
      const birthDate = new Date(year, month - 1, day); const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
      
      await setDoc(userRef, { firstName: firstName.trim(), lastName: lastName.trim(), displayName: displayNameValue, birthDate: `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`, age: age, profileComplete: true, profileCompletedAt: serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true });
      await updateProfile(currentUser, { displayName: displayNameValue });
      await setDoc(userRef, { subscriptionStatus: 'pending_payment', pendingPlan: { planType: selectedPlanData.planType, billingCycle: selectedPlanData.billingCycle, price: selectedPlanData.price, name: selectedPlanData.name, squareLink: selectedPlanData.squareLink, selectedAt: serverTimestamp() }, updatedAt: serverTimestamp() }, { merge: true });
      
      const squareLink = selectedPlanData.squareLink;
      if (squareLink) { sessionStorage.setItem('pendingUserId', currentUser.uid); window.location.href = squareLink; }
      else { setError('Error loading payment page. Please try again.'); setIsSubmitting(false); }
    } catch (error) { console.error('Error saving profile and plan:', error); setError('Failed to save. Please try again.'); setIsSubmitting(false); }
  };

  const handleClose = () => { if (window.opener) window.close(); else navigate('/'); };

  // ============================================
  // PROFILE COMPLETION FORM (3 STEPS)
  // ============================================
  if (showProfileCompletion && currentUser) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#FAFAFA', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '30px 20px 20px 20px', boxSizing: 'border-box', overflowY: 'auto' }}>
        <Helmet><title>Complete Your Profile | Outlne</title><meta name="robots" content="noindex, nofollow" /></Helmet>
        <div style={{ position: 'absolute', top: '20px', right: '25px', fontSize: '2rem', color: '#333333', cursor: 'pointer', zIndex: 10 }} onClick={handleClose}>×</div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: profileStep === 3 ? '1000px' : '360px', margin: '0 auto', padding: profileStep === 3 ? '0 20px' : '0' }}>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '30px' }}>
            {[1, 2, 3].map(step => (<div key={step} style={{ width: '50px', height: '4px', borderRadius: '2px', backgroundColor: profileStep >= step ? '#88B7FF' : '#DDDDDD', transition: 'background-color 0.3s ease' }} />))}
          </div>
          <h2 style={{ color: '#333333', fontSize: '1.5rem', fontFamily: 'avenir-lt-pro, sans-serif', fontWeight: '500', marginBottom: '8px', textAlign: 'center' }}>{profileStep === 1 ? 'What should we call you?' : profileStep === 2 ? 'When were you born?' : 'Choose your plan'}</h2>
          <p style={{ color: '#666666', fontSize: '0.9rem', fontFamily: 'avenir-lt-pro, sans-serif', marginBottom: '30px', textAlign: 'center' }}>Step {profileStep} of 3</p>

          {profileStep === 1 && (
            <form onSubmit={handleProfileStep1} style={{ width: '100%' }}>
              <input type="text" placeholder="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required disabled={isSubmitting} style={{ width: '100%', padding: '14px 16px', backgroundColor: '#FFFFFF', border: '1px solid #DDDDDD', borderRadius: '8px', color: '#333333', fontSize: '1rem', fontFamily: 'avenir-lt-pro, sans-serif', marginBottom: '15px', boxSizing: 'border-box', outline: 'none' }} />
              <input type="text" placeholder="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} required disabled={isSubmitting} style={{ width: '100%', padding: '14px 16px', backgroundColor: '#FFFFFF', border: '1px solid #DDDDDD', borderRadius: '8px', color: '#333333', fontSize: '1rem', fontFamily: 'avenir-lt-pro, sans-serif', marginBottom: '15px', boxSizing: 'border-box', outline: 'none' }} />
              <input type="text" placeholder="Display Name / Studio Name (Optional)" value={displayName} onChange={(e) => setDisplayName(e.target.value)} disabled={isSubmitting} style={{ width: '100%', padding: '14px 16px', backgroundColor: '#FFFFFF', border: '1px solid #DDDDDD', borderRadius: '8px', color: '#333333', fontSize: '1rem', fontFamily: 'avenir-lt-pro, sans-serif', marginBottom: error ? '10px' : '30px', boxSizing: 'border-box', outline: 'none' }} />
              {error && <p style={{ color: '#E53935', fontSize: '0.85rem', marginBottom: '15px', textAlign: 'center', fontFamily: 'avenir-lt-pro, sans-serif' }}>{error}</p>}
              <button type="submit" disabled={isSubmitting} style={{ width: '100%', padding: '14px', backgroundColor: isSubmitting ? '#CCCCCC' : '#88B7FF', color: '#FFFFFF', border: 'none', borderRadius: '30px', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1rem', fontWeight: '500', cursor: isSubmitting ? 'not-allowed' : 'pointer', marginBottom: '15px' }}>Continue</button>
            </form>
          )}

          {profileStep === 2 && (
            <form onSubmit={handleProfileStep2} style={{ width: '100%' }}>
              <p style={{ color: '#666666', fontSize: '0.9rem', fontFamily: 'avenir-lt-pro, sans-serif', marginBottom: '20px', textAlign: 'center' }}>You must be at least 13 years old</p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '30px' }}>
                <input type="text" placeholder="MM" value={birthMonth} onChange={(e) => setBirthMonth(e.target.value.replace(/\D/g, '').slice(0, 2))} maxLength="2" required disabled={isSubmitting} style={{ width: '80px', padding: '14px 8px', backgroundColor: '#FFFFFF', border: '1px solid #DDDDDD', borderRadius: '8px', color: '#333333', fontSize: '1rem', fontFamily: 'avenir-lt-pro, sans-serif', textAlign: 'center', boxSizing: 'border-box', outline: 'none' }} />
                <input type="text" placeholder="DD" value={birthDay} onChange={(e) => setBirthDay(e.target.value.replace(/\D/g, '').slice(0, 2))} maxLength="2" required disabled={isSubmitting} style={{ width: '80px', padding: '14px 8px', backgroundColor: '#FFFFFF', border: '1px solid #DDDDDD', borderRadius: '8px', color: '#333333', fontSize: '1rem', fontFamily: 'avenir-lt-pro, sans-serif', textAlign: 'center', boxSizing: 'border-box', outline: 'none' }} />
                <input type="text" placeholder="YYYY" value={birthYear} onChange={(e) => setBirthYear(e.target.value.replace(/\D/g, '').slice(0, 4))} maxLength="4" required disabled={isSubmitting} style={{ width: '100px', padding: '14px 8px', backgroundColor: '#FFFFFF', border: '1px solid #DDDDDD', borderRadius: '8px', color: '#333333', fontSize: '1rem', fontFamily: 'avenir-lt-pro, sans-serif', textAlign: 'center', boxSizing: 'border-box', outline: 'none' }} />
              </div>
              <p style={{ color: '#999999', fontSize: '0.8rem', fontFamily: 'avenir-lt-pro, sans-serif', marginTop: '-15px', marginBottom: '30px', textAlign: 'center' }}>Month / Day / Year</p>
              {error && <p style={{ color: '#E53935', fontSize: '0.85rem', marginBottom: '15px', textAlign: 'center', fontFamily: 'avenir-lt-pro, sans-serif' }}>{error}</p>}
              <button type="submit" disabled={isSubmitting} style={{ width: '100%', padding: '14px', backgroundColor: isSubmitting ? '#CCCCCC' : '#88B7FF', color: '#FFFFFF', border: 'none', borderRadius: '30px', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1rem', fontWeight: '500', cursor: isSubmitting ? 'not-allowed' : 'pointer', marginBottom: '15px' }}>Continue</button>
              <button type="button" onClick={() => setProfileStep(1)} disabled={isSubmitting} style={{ width: '100%', padding: '10px', backgroundColor: 'transparent', color: '#88B7FF', border: 'none', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.9rem', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>← Back</button>
            </form>
          )}

          {profileStep === 3 && (
            <form onSubmit={handlePlanSelection} style={{ width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '30px', padding: '0.5rem', backgroundColor: '#F0F0F0', borderRadius: '50px', border: '1px solid #DDDDDD', maxWidth: '300px', width: '100%', marginLeft: 'auto', marginRight: 'auto' }}>
                <span style={{ color: billingCycle === 'monthly' ? '#333333' : '#999999', padding: '0.5rem 1rem', fontSize: '0.9rem', fontWeight: billingCycle === 'monthly' ? '600' : '400', cursor: 'pointer', fontFamily: 'avenir-lt-pro, sans-serif' }} onClick={() => setBillingCycle('monthly')}>Monthly</span>
                <div style={{ width: '40px', height: '24px', borderRadius: '12px', backgroundColor: billingCycle === 'monthly' ? '#CCCCCC' : '#88B7FF', position: 'relative', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '2px', transition: 'all 0.3s ease' }} onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}>
                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'white', position: 'absolute', left: billingCycle === 'monthly' ? '2px' : '18px', transition: 'left 0.3s ease' }} />
                </div>
                <span style={{ color: billingCycle === 'annual' ? '#333333' : '#999999', padding: '0.5rem 1rem', fontSize: '0.9rem', fontWeight: billingCycle === 'annual' ? '600' : '400', cursor: 'pointer', fontFamily: 'avenir-lt-pro, sans-serif' }} onClick={() => setBillingCycle('annual')}>Annual</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'stretch', gap: '16px', width: '100%', marginBottom: '30px', flexWrap: 'wrap' }}>
                {PLANS[billingCycle].map((plan, index) => (
                  <div key={index} onClick={() => setSelectedPlanIndex(index)} onMouseEnter={() => setHoveredPlanIndex(index)} onMouseLeave={() => setHoveredPlanIndex(null)}
                    style={{ backgroundColor: '#FFFFFF', border: selectedPlanIndex === index ? '2px solid #88B7FF' : hoveredPlanIndex === index ? '2px solid rgba(136, 183, 255, 0.5)' : '1px solid #DDDDDD', borderRadius: '16px', padding: '20px 16px', width: '100%', maxWidth: '220px', display: 'flex', flexDirection: 'column', cursor: 'pointer', transition: 'all 0.3s ease', transform: selectedPlanIndex === index || hoveredPlanIndex === index ? 'translateY(-4px)' : 'none', boxShadow: selectedPlanIndex === index ? '0 8px 20px rgba(136, 183, 255, 0.2)' : hoveredPlanIndex === index ? '0 4px 12px rgba(0, 0, 0, 0.08)' : '0 2px 8px rgba(0, 0, 0, 0.04)', flex: '1' }}>
                    <h3 style={{ fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1.2rem', fontWeight: '600', color: '#333333', marginBottom: '4px' }}>{plan.name}</h3>
                    <p style={{ color: '#666666', fontSize: '0.75rem', marginBottom: '12px', lineHeight: '1.4', fontFamily: 'avenir-lt-pro, sans-serif' }}>{plan.desc}</p>
                    <div style={{ marginBottom: '12px' }}><p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#333333', margin: 0, fontFamily: 'avenir-lt-pro, sans-serif' }}>{plan.price}<span style={{ fontSize: '0.8rem', fontWeight: 'normal', color: '#888888' }}>/mo</span></p>{billingCycle === 'annual' && plan.saving && <p style={{ color: '#88B7FF', fontSize: '0.75rem', margin: '4px 0 0 0', fontFamily: 'avenir-lt-pro, sans-serif' }}>Save {plan.saving} annually</p>}</div>
                    <div style={{ overflowY: 'auto', maxHeight: '200px', marginBottom: '16px', paddingRight: '4px', flexGrow: 1 }}>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: '#555555', fontSize: '0.75rem', fontFamily: 'avenir-lt-pro, sans-serif' }}>
                        {plan.features.map((feature, fi) => (<li key={fi} style={{ marginBottom: '6px', paddingLeft: '16px', position: 'relative', lineHeight: '1.3' }}><span style={{ position: 'absolute', left: 0, color: '#88B7FF', fontWeight: 'bold', fontSize: '0.7rem' }}>✓</span>{feature}</li>))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
              {error && <p style={{ color: '#E53935', fontSize: '0.85rem', marginBottom: '15px', textAlign: 'center', fontFamily: 'avenir-lt-pro, sans-serif' }}>{error}</p>}
              <p style={{ color: '#666666', fontSize: '0.8rem', marginBottom: '20px', textAlign: 'center', fontFamily: 'avenir-lt-pro, sans-serif' }}>You'll be redirected to our secure payment page to complete your account setup</p>
              <button type="submit" disabled={isSubmitting} style={{ width: '100%', maxWidth: '300px', padding: '14px', backgroundColor: isSubmitting ? '#CCCCCC' : '#88B7FF', color: '#FFFFFF', border: 'none', borderRadius: '30px', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1rem', fontWeight: '500', cursor: isSubmitting ? 'not-allowed' : 'pointer', marginBottom: '15px', marginLeft: 'auto', marginRight: 'auto', display: 'block', opacity: isSubmitting ? 0.7 : 1 }}>{isSubmitting ? 'Processing...' : 'Continue to Payment'}</button>
              <button type="button" onClick={() => setProfileStep(2)} disabled={isSubmitting} style={{ width: '100%', padding: '10px', backgroundColor: 'transparent', color: '#88B7FF', border: 'none', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.9rem', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>← Back</button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // ============================================
  // PASSWORD RESET FORM
  // ============================================
  if (resetPasswordMode) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#FAFAFA', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '30px 20px', boxSizing: 'border-box' }}>
        <Helmet><title>Reset Password | Outlne</title></Helmet>
        <div style={{ position: 'absolute', top: '20px', right: '25px', fontSize: '2rem', color: '#333333', cursor: 'pointer' }} onClick={handleClose}>×</div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '360px' }}>
          <h2 style={{ color: '#333333', fontSize: '1.5rem', fontFamily: 'avenir-lt-pro, sans-serif', fontWeight: '500', marginBottom: '8px', textAlign: 'center' }}>Reset Password</h2>
          <p style={{ color: '#666666', fontSize: '0.9rem', fontFamily: 'avenir-lt-pro, sans-serif', marginBottom: '30px', textAlign: 'center' }}>Enter your email to receive a password reset link</p>
          <form onSubmit={handlePasswordReset} style={{ width: '100%' }}>
            <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required style={{ width: '100%', padding: '14px 16px', backgroundColor: '#FFFFFF', border: '1px solid #DDDDDD', borderRadius: '8px', color: '#333333', fontSize: '1rem', fontFamily: 'avenir-lt-pro, sans-serif', marginBottom: error || successMessage ? '10px' : '30px', boxSizing: 'border-box', outline: 'none' }} />
            {error && <p style={{ color: '#E53935', fontSize: '0.85rem', marginBottom: '15px', textAlign: 'center', fontFamily: 'avenir-lt-pro, sans-serif' }}>{error}</p>}
            {successMessage && <p style={{ color: '#4CAF50', fontSize: '0.85rem', marginBottom: '15px', textAlign: 'center', fontFamily: 'avenir-lt-pro, sans-serif' }}>{successMessage}</p>}
            <button type="submit" style={{ width: '100%', padding: '14px', backgroundColor: '#88B7FF', color: '#FFFFFF', border: 'none', borderRadius: '30px', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1rem', fontWeight: '500', cursor: 'pointer', marginBottom: '15px' }}>Send Reset Email</button>
            <button type="button" onClick={() => { setResetPasswordMode(false); setError(''); setSuccessMessage(''); }} style={{ width: '100%', padding: '10px', backgroundColor: 'transparent', color: '#88B7FF', border: 'none', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.9rem', cursor: 'pointer' }}>← Back to Sign In</button>
          </form>
        </div>
      </div>
    );
  }

  // ============================================
  // FULL AUTH FLOW SCREEN (only accessible after dev login)
  // ============================================
  if (showFullAuth && isDevAuthenticated) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#FAFAFA', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '30px 20px 20px 20px', boxSizing: 'border-box', overflowY: 'auto' }}>
        <Helmet><title>{selectedPlan ? `Sign Up - ${selectedPlan.name} Plan` : 'Sign Up | Outlne'}</title><meta name="robots" content="noindex, nofollow" /></Helmet>
        <div style={{ position: 'absolute', top: '20px', right: '25px', fontSize: '2rem', color: '#333333', cursor: 'pointer', zIndex: 10 }} onClick={handleClose}>×</div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '360px', margin: '0 auto' }}>
          {/* Back to dev panel button */}
          <button onClick={() => setShowFullAuth(false)}
            style={{ position: 'absolute', top: '20px', left: '25px', background: 'none', border: 'none', color: '#999', fontSize: '0.8rem', fontFamily: 'avenir-lt-pro, sans-serif', cursor: 'pointer' }}>
            ← Dev Panel
          </button>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '30px' }}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', padding: '3px', backgroundColor: '#FFFFFF', border: '2px solid #88B7FF', boxSizing: 'border-box', overflow: 'hidden' }}>
              <img src={DefaultAvatarIcon} alt="User" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', display: 'block' }} />
            </div>
            <div style={{ color: '#333333', fontSize: '1.3rem', textAlign: 'center', lineHeight: '1.4', marginTop: '15px', fontFamily: 'avenir-lt-pro, sans-serif', fontWeight: '500' }}>
              {authMode === 'signup' ? 'Create Account' : 'Welcome Back'}
            </div>
            {selectedPlan && <div style={{ color: '#88B7FF', fontSize: '0.9rem', marginTop: '8px', fontFamily: 'avenir-lt-pro, sans-serif', backgroundColor: '#F0F7FF', padding: '4px 12px', borderRadius: '20px' }}>{selectedPlan.name} Plan Selected</div>}
          </div>

          <div style={{ display: 'flex', gap: '30px', justifyContent: 'center', width: '100%', marginBottom: '30px' }}>
            <button onClick={() => setAuthMode('signup')} style={{ background: 'none', border: 'none', color: authMode === 'signup' ? '#333333' : '#999999', fontSize: '1.1rem', fontFamily: 'avenir-lt-pro, sans-serif', fontWeight: '500', cursor: 'pointer', padding: '5px 0', borderBottom: authMode === 'signup' ? '2px solid #88B7FF' : '2px solid transparent', transition: 'all 0.3s ease' }}>Sign Up</button>
            <button onClick={() => setAuthMode('login')} style={{ background: 'none', border: 'none', color: authMode === 'login' ? '#333333' : '#999999', fontSize: '1.1rem', fontFamily: 'avenir-lt-pro, sans-serif', fontWeight: '500', cursor: 'pointer', padding: '5px 0', borderBottom: authMode === 'login' ? '2px solid #88B7FF' : '2px solid transparent', transition: 'all 0.3s ease' }}>Log In</button>
          </div>

          <button onClick={handleGoogleSignIn} style={{ width: '100%', padding: '14px', backgroundColor: '#FFFFFF', color: '#333333', border: '1px solid #DDDDDD', borderRadius: '30px', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1rem', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '20px' }}>
            <svg width="20" height="20" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </button>

          <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '10px', marginBottom: '20px' }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: '#DDDDDD' }} /><span style={{ color: '#999999', fontSize: '0.85rem', fontFamily: 'avenir-lt-pro, sans-serif' }}>or</span><div style={{ flex: 1, height: '1px', backgroundColor: '#DDDDDD' }} />
          </div>

          <form onSubmit={authMode === 'signup' ? handleEmailSignUp : handleEmailLogin} style={{ width: '100%' }}>
            <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required style={{ width: '100%', padding: '14px 16px', backgroundColor: '#FFFFFF', border: '1px solid #DDDDDD', borderRadius: '8px', color: '#333333', fontSize: '1rem', fontFamily: 'avenir-lt-pro, sans-serif', marginBottom: '15px', boxSizing: 'border-box', outline: 'none' }} />
            <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} style={{ width: '100%', padding: '14px 16px', backgroundColor: '#FFFFFF', border: '1px solid #DDDDDD', borderRadius: '8px', color: '#333333', fontSize: '1rem', fontFamily: 'avenir-lt-pro, sans-serif', marginBottom: error ? '10px' : '25px', boxSizing: 'border-box', outline: 'none' }} />
            {error && <p style={{ color: '#E53935', fontSize: '0.85rem', marginBottom: '15px', textAlign: 'center', fontFamily: 'avenir-lt-pro, sans-serif' }}>{error}</p>}
            <button type="submit" style={{ width: '100%', padding: '14px', backgroundColor: '#88B7FF', color: '#FFFFFF', border: 'none', borderRadius: '30px', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1rem', fontWeight: '500', cursor: 'pointer', marginBottom: '10px' }}>{authMode === 'signup' ? 'Sign Up with Email' : 'Log In with Email'}</button>
          </form>

          {authMode === 'login' && (
            <button type="button" onClick={() => setResetPasswordMode(true)} style={{ width: '100%', padding: '8px', backgroundColor: 'transparent', color: '#88B7FF', border: 'none', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.85rem', cursor: 'pointer', marginBottom: '20px' }}>Forgot Password?</button>
          )}

          <div style={{ color: '#999999', fontSize: '0.75rem', textAlign: 'center', maxWidth: '300px', lineHeight: '1.4', marginTop: '10px', fontFamily: 'avenir-lt-pro, sans-serif' }}>
            By continuing, you agree to our<br />
            <a href="/terms" onClick={(e) => { e.preventDefault(); navigate('/terms'); }} style={{ color: '#88B7FF', textDecoration: 'none' }}>Terms of Service</a> and{' '}
            <a href="/privacy" onClick={(e) => { e.preventDefault(); navigate('/privacy'); }} style={{ color: '#88B7FF', textDecoration: 'none' }}>Privacy Policy</a>.
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // DEFAULT: DEV LOGIN SCREEN
  // (Shows options only after successful dev login)
  // ============================================
  return (
    <div style={{
      position: 'fixed', top: -60, left: 0, width: '100vw', height: '100vh',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      background: 'white', zIndex: 9999,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '2rem', boxSizing: 'border-box'
    }}>
      <div style={{ transform: 'scale(0.85)', transformOrigin: 'center', width: '100%', maxWidth: '400px' }}>
        <form onSubmit={handleDevSubmit} style={{
          background: 'rgba(255, 255, 255, 0.95)', padding: '2.5rem 2rem',
          borderRadius: '20px', boxShadow: 'none', textAlign: 'center',
          width: '100%', backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0, 0, 0, 0.2)', boxSizing: 'border-box'
        }}>
          {/* ============================================ */}
          {/* NOT AUTHENTICATED - Show login form */}
          {/* ============================================ */}
          {!isDevAuthenticated ? (
            <>
              <div style={{ marginBottom: '2rem' }}>
                <h2 style={{ margin: '0 0 1rem 0', fontFamily: 'Menlo', fontSize: '0.9rem', color: '#333', fontWeight: '600' }}>Developer Login Required</h2>
                <p style={{ margin: 0, fontFamily: 'Menlo', fontSize: '0.7rem', color: '#666', lineHeight: '1.5' }}>Please enter your credentials to access</p>
              </div>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <input type="email" value={devEmail} onChange={(e) => setDevEmail(e.target.value)} placeholder="Email address"
                  style={{ width: '100%', padding: '1rem 1.5rem', marginBottom: '1rem', fontFamily: 'Menlo', fontSize: '16px', border: `2px solid ${devError ? '#ff4757' : '#e1e5e9'}`, borderRadius: '12px', outline: 'none', transition: 'all 0.3s ease', background: '#fff', boxSizing: 'border-box' }} autoFocus />
                <input type="password" value={devPassword} onChange={(e) => setDevPassword(e.target.value)} placeholder="Password"
                  style={{ width: '100%', padding: '1rem 1.5rem', fontFamily: 'Menlo', fontSize: '16px', border: `2px solid ${devError ? '#ff4757' : '#e1e5e9'}`, borderRadius: '12px', outline: 'none', transition: 'all 0.3s ease', background: '#fff', boxSizing: 'border-box' }} />
              </div>
              
              {devError && (
                <div style={{ color: '#ff4757', margin: '1rem 0', padding: '0.75rem', background: 'rgba(255, 71, 87, 0.1)', borderRadius: '8px', fontFamily: 'Menlo', fontSize: '0.9rem' }}>{devError}</div>
              )}
              
              <button type="submit" style={{ width: '50%', padding: '1rem 1.5rem', background: '#5494f9', color: 'white', fontFamily: 'Menlo', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600', transition: 'all 0.3s ease' }}>Login</button>
              
              <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #ffffffff' }}>
                <p style={{ margin: 0, color: '#888', fontFamily: 'Menlo', fontSize: '0.7rem', lineHeight: '11px' }}>
                  Contact developers if you've forgotten your credentials
                  <br /><br />© 2026 Outlne All Rights Reserved
                </p>
              </div>
            </>
          ) : (
            /* ============================================ */
            /* AUTHENTICATED - Show action buttons */
            /* ============================================ */
            <>
              <div style={{ marginBottom: '2rem' }}>
                <div style={{ width: '60px', height: '60px', borderRadius: '50%', backgroundColor: '#5494f9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem auto' }}>
                  <span style={{ color: 'white', fontSize: '1.5rem' }}>✓</span>
                </div>
                <h2 style={{ margin: '0 0 0.5rem 0', fontFamily: 'Menlo', fontSize: '0.9rem', color: '#333', fontWeight: '600' }}>Authenticated</h2>
                <p style={{ margin: 0, fontFamily: 'Menlo', fontSize: '0.7rem', color: '#666', lineHeight: '1.5' }}>Choose an option below</p>
              </div>
              
              {/* Go to Dashboard Button */}
              <button type="button" onClick={handleGoToDashboard}
                style={{ width: '100%', padding: '1rem', marginBottom: '0.75rem', background: '#5494f9', color: 'white', fontFamily: 'Menlo', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600', transition: 'all 0.3s ease' }}>
                Go to Dashboard
              </button>
              
              {/* Test Full Auth Flow Button */}
              <button type="button" onClick={() => setShowFullAuth(true)}
                style={{ width: '100%', padding: '0.8rem', marginBottom: '1rem', background: 'transparent', color: '#5494f9', fontFamily: 'Menlo', border: '2px solid #5494f9', borderRadius: '12px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600', transition: 'all 0.3s ease' }}>
                Test Full Auth Flow
              </button>
              
              {/* Sign Out / Back */}
              <button type="button" onClick={() => { setIsDevAuthenticated(false); setDevEmail(''); setDevPassword(''); }}
                style={{ width: '100%', padding: '0.8rem', background: 'transparent', color: '#999', fontFamily: 'Menlo', border: '1px dashed #ccc', borderRadius: '12px', cursor: 'pointer', fontSize: '0.75rem' }}>
                Sign Out
              </button>
              
              <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #ffffffff' }}>
                <p style={{ margin: 0, color: '#888', fontFamily: 'Menlo', fontSize: '0.7rem', lineHeight: '11px' }}>
                  Developer access granted
                  <br /><br />© 2026 Outlne All Rights Reserved
                </p>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default Developer;